import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, CheckCircle2, Cloud, Users, BarChart3,
  Server, Database, Network, Cpu, Building2, GraduationCap,
  Presentation, UserCog, ChevronDown,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Eyebrow } from "../components/Eyebrow";
import { Button } from "../components/ui/button";
import { PARTNERS } from "../mock";
import { LogoImage } from "../components/LogoImage";
import { PRODUCTS_DATA } from "../data/productsData";

const ACCENTS = [
  "from-[#15348C] to-[#0A1F5C]",
  "from-[#6BBE32] to-[#4F9622]",
  "from-[#8BD835] to-[#6BBE32]",
  "from-[#4F9622] to-[#15348C]",
];

const USE_CASE_ICONS = [Building2, Cpu, GraduationCap, Presentation, UserCog];

const FaqItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-[#15348C]">{q}</span>
        <ChevronDown size={18} className={`shrink-0 text-[#6BBE32] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{a}</div>}
    </div>
  );
};

const CloudLabsPage = () => {
  const data = PRODUCTS_DATA["cloud-labs"];
  const {
    eyebrow, title, subtitle, highlights, stats,
    whyMattersHeading, whyMatters,
    whatHeading, whatCards,
    capabilitiesHeading, capabilities,
    howHeading, howSteps,
    whoHeading, whoItems,
    faqs,
    ctaHeadline, ctaSupporting,
  } = data;

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO — split layout */}
      <section className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="sg-grid-drift absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />
        <div className="sg-float-a pointer-events-none absolute -top-24 right-0 w-96 h-96 bg-[#6BBE32]/15 rounded-full blur-3xl" />
        <div className="sg-float-b pointer-events-none absolute -bottom-24 -left-10 w-80 h-80 bg-[#8BD835]/15 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <div className="flex"><Eyebrow dark>{eyebrow}</Eyebrow></div>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">{title}</h1>
            <p className="mt-5 text-gray-200 text-lg leading-relaxed max-w-xl">{subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/offerings/all">
                <Button className="sg-btn-glow bg-[#6BBE32] hover:bg-[#4F9622] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#6BBE32]/30">
                  Explore Cloud Labs <ArrowRight size={17} className="ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-transparent border-2 border-white/40 hover:bg-white/10 hover:border-white text-white font-semibold px-6 h-12 rounded-md">
                  Talk to Our Team
                </Button>
              </Link>
            </div>
            {highlights && highlights.length > 0 && (
              <div className="mt-9 flex flex-wrap gap-x-8 gap-y-3">
                {[Cloud, Users, BarChart3].map((Icon, i) => (
                  <div key={highlights[i] || i} className="flex items-center gap-2 text-sm text-gray-300">
                    <Icon size={16} className="text-[#8BD835]" /> {highlights[i]}
                  </div>
                ))}
              </div>
            )}
          </Reveal>

          {/* Illustrative dashboard mockup — icons only, no photography */}
          <Reveal variant="right" delay={120}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-4 bg-gradient-to-br from-[#6BBE32]/20 to-[#8BD835]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-3 bg-[#F8F9FA] border-b border-gray-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-3 text-xs text-gray-400 font-medium">cloud-lab-session</span>
                </div>
                <div className="p-6 grid grid-cols-2 gap-4">
                  {[
                    { icon: Server, label: "Compute" },
                    { icon: Database, label: "Storage" },
                    { icon: Network, label: "Networking" },
                    { icon: Cpu, label: "Database" },
                  ].map((s, i) => (
                    <div key={s.label} className={`rounded-xl p-4 bg-gradient-to-br ${ACCENTS[i % ACCENTS.length]} text-white`}>
                      <s.icon size={22} />
                      <div className="mt-3 text-sm font-semibold">{s.label}</div>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-6">
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="sg-shimmer h-full w-2/3 bg-gradient-to-r from-[#6BBE32] to-[#8BD835]" />
                  </div>
                  <div className="mt-2 text-xs text-gray-400">Provisioning environment…</div>
                </div>
              </div>
              <div className="sg-chip absolute -top-5 -right-5 bg-white rounded-xl shadow-xl px-4 py-2 text-xs font-bold text-[#15348C] border border-gray-100" style={{ animationDelay: "300ms" }}>
                Practice · Experiment · Apply
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2) STATS STRIP */}
      {stats && stats.length > 0 && (
        <section className="py-4 relative z-10 -mt-8">
          <div className="max-w-6xl mx-auto px-4">
            <Reveal variant="scale">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 rounded-2xl border border-gray-100 bg-white shadow-xl shadow-[#15348C]/10">
                {stats.map((s) => (
                  <div key={s.label} className="p-6 text-center group">
                    <div className="text-3xl lg:text-4xl font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{s.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* 3) THE CHALLENGE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left">
            <Eyebrow color="#4F9622">THE CHALLENGE</Eyebrow>
            <h2 className="text-[#15348C] leading-tight">{whyMattersHeading}</h2>
            <div className="mt-6 space-y-3">
              {whyMatters.map((w) => (
                <div key={w} className="flex gap-3">
                  <CheckCircle2 size={18} className="text-[#6BBE32] shrink-0 mt-0.5" />
                  <p className="text-gray-600 leading-relaxed">{w}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="relative">
              <div className="sg-float-c pointer-events-none absolute -inset-6 bg-gradient-to-br from-[#15348C]/10 to-[#6BBE32]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-gradient-to-br from-[#F8F9FA] to-[#EFF3E8] border border-gray-100 rounded-2xl p-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] flex items-center justify-center shadow-lg shadow-[#15348C]/30 mb-5">
                  <ArrowRight size={26} className="text-white" />
                </div>
                <div className="relative inline-block">
                  <span className="text-[#15348C] text-xl font-semibold">From learning to doing.</span>
                  <span className="block h-0.5 w-full bg-[#8BD835] mt-1 rounded-full" />
                </div>
                <p className="mt-4 text-gray-500 text-sm max-w-xs">Every Cloud Labs session moves a learner from theory straight into a real, hands-on environment.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4) WHAT YOU GET */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div className="max-w-2xl">
                <Eyebrow color="#6BBE32">WHAT YOU GET</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{whatHeading}</h2>
              </div>
              <Link to="/offerings/all" className="hidden sm:inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                Explore Cloud Labs <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {whatCards.map((c, i) => {
              const Icon = c.icon;
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <Reveal key={c.title} variant="up" delay={i * 100}>
                  <div className="group bg-white border border-gray-100 rounded-2xl p-7 h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg mb-5 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={26} className="text-white" />
                    </div>
                    <h3 className="text-[#15348C]" style={{ fontSize: "1.2rem" }}>{c.title}</h3>
                    <p className="mt-3 text-gray-600 leading-relaxed text-sm">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5) KEY CAPABILITIES — dark tile grid */}
      <section className="py-20 bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="sg-grid-drift absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />
        <div className="relative max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div className="max-w-2xl">
                <Eyebrow dark>EXPLORE</Eyebrow>
                <h2 className="text-white leading-tight">{capabilitiesHeading}</h2>
              </div>
              <Link to="/offerings/all" className="hidden sm:inline-flex items-center gap-1.5 text-[#8BD835] font-semibold hover:text-white transition-colors">
                View All Capabilities <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} variant="up" delay={i * 70}>
                  <div className="group bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#8BD835]/40 transition-all duration-300">
                    <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:bg-[#8BD835] transition-colors">
                      <Icon size={20} className="text-[#8BD835] group-hover:text-[#0A1F5C] transition-colors" />
                    </div>
                    <div className="font-semibold">{c.title}</div>
                    <p className="mt-1 text-sm text-gray-300">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6) USE CASES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <Eyebrow color="#4F9622">USE CASES</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{whoHeading}</h2>
            </div>
          </Reveal>
          <div className={`grid gap-6 ${whoItems.length > 3 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {whoItems.map((w, i) => {
              const Icon = USE_CASE_ICONS[i % USE_CASE_ICONS.length];
              return (
                <Reveal key={w.title} variant="up" delay={i * 90}>
                  <div className="group bg-white border border-gray-100 rounded-2xl overflow-hidden h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className={`h-24 flex items-center justify-center bg-gradient-to-br ${ACCENTS[i % ACCENTS.length]}`}>
                      <Icon size={32} className="text-white" />
                    </div>
                    <div className="p-5">
                      <h3 className="text-[#15348C]" style={{ fontSize: "1.05rem" }}>{w.title}</h3>
                      <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7) HOW IT WORKS — stepper */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <Eyebrow color="#6BBE32">HOW IT WORKS</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{howHeading}</h2>
            </div>
          </Reveal>
          <div className="flex flex-col lg:flex-row items-stretch lg:items-start gap-6 lg:gap-0">
            {howSteps.map((step, i) => (
              <React.Fragment key={step.title}>
                <Reveal variant="up" delay={i * 100} className="flex-1">
                  <div className="text-center px-2">
                    <div
                      className="mx-auto w-12 h-12 rounded-full text-white font-bold flex items-center justify-center shadow-lg"
                      style={{ background: ["#15348C", "#6BBE32", "#4F9622", "#0A1F5C"][i % 4] }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-4 text-[#15348C]" style={{ fontSize: "1.05rem" }}>{step.title}</h3>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </Reveal>
                {i < howSteps.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center pt-6 text-gray-300">
                    <ArrowRight size={20} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 8) TRUSTED BY */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center text-xs font-bold tracking-widest text-gray-500 mb-8">TRUSTED BY LEADING ORGANIZATIONS</div>
            <div
              className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-80"
              style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
            >
              {PARTNERS.slice(0, 10).map((p) => (
                <LogoImage key={p.name} src={p.logo} alt={p.name}
                  className="h-8 max-w-[120px] object-contain"
                  fallbackClassName="text-[#15348C] font-bold text-sm" />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9) FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="py-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                  <Eyebrow color="#4F9622">FAQS</Eyebrow>
                  <h2 className="text-[#15348C] leading-tight">Quick Answers</h2>
                </div>
                <Link to="/contact" className="hidden sm:inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                  Still have questions? Contact our team <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-4">
              {faqs.map((f, i) => (
                <Reveal key={f.q} variant="up" delay={i * 60}>
                  <FaqItem q={f.q} a={f.a} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 10) FINAL CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] px-8 py-12 lg:px-14 lg:py-16 text-white">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "20px 20px" }}
              />
              <div className="sg-float-a pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="sg-float-c pointer-events-none absolute -bottom-24 -left-24 w-80 h-80 bg-[#8BD835]/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <Eyebrow dark>READY TO GET STARTED?</Eyebrow>
                  <h2 className="text-white leading-tight">{ctaHeadline}</h2>
                  {ctaSupporting && <p className="mt-3 text-gray-300">{ctaSupporting}</p>}
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end items-center">
                  <Link to="/contact">
                    <Button className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-7 h-12 rounded-md shadow-lg shadow-[#8BD835]/30">
                      Talk to Our Team <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="mailto:info@skillsgain.com" className="inline-flex items-center gap-2 text-white/90 hover:text-[#8BD835] font-semibold transition-colors">
                    <Mail size={18} /> info@skillsgain.com
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CloudLabsPage;
